/**
 * Where to show `content: [Buffer]` hints in a transformation input (JSON
 * text): the offset of each file part's `"size"` key. File parts of a
 * multipart/form-data body are objects (or arrays of objects, for repeated
 * fields) under `body` with a numeric `size` and a `filename`. Their bytes
 * reach the transformation as `content`, but aren't part of the input.
 */
type PathKey = string | number | null;
type Frame =
  | { kind: 'object'; key: PathKey; current_key: string | null; expecting_key: boolean }
  | { kind: 'array'; key: PathKey; index: number };

export const multipartFileHintOffsets = (text: string): number[] => {
  let root: unknown;
  try {
    root = JSON.parse(text);
  } catch (_) {
    return [];
  }
  const request = root as { headers?: Record<string, unknown> } | null;
  const content_type = Object.entries(request?.headers ?? {}).find(
    ([key]) => key.toLowerCase() === 'content-type',
  )?.[1];
  if (typeof content_type !== 'string' || !/^multipart\/form-data/i.test(content_type)) {
    return [];
  }

  const isFilePart = (path: PathKey[]) => {
    if (path[0] !== 'body' || (path.length !== 2 && path.length !== 3)) return false;
    let value: unknown = root;
    for (const key of path) value = (value as Record<string, unknown>)?.[key as string];
    const part = value as Record<string, unknown> | null;
    return (
      !!part &&
      typeof part === 'object' &&
      !Array.isArray(part) &&
      typeof part.size === 'number' &&
      'filename' in part
    );
  };

  const stack: Frame[] = [];
  const offsets: number[] = [];
  const top = () => stack[stack.length - 1];
  // Key under which a value starting now sits in its parent.
  const valueKey = (): PathKey => {
    const parent = top();
    if (!parent) return null;
    return parent.kind === 'object' ? parent.current_key : parent.index;
  };

  let i = 0;
  while (i < text.length) {
    const char = text[i];
    if (char === '{') {
      stack.push({ kind: 'object', key: valueKey(), current_key: null, expecting_key: true });
    } else if (char === '[') {
      stack.push({ kind: 'array', key: valueKey(), index: 0 });
    } else if (char === '}' || char === ']') {
      stack.pop();
    } else if (char === ',') {
      const frame = top();
      if (frame?.kind === 'array') frame.index += 1;
      else if (frame) frame.expecting_key = true;
    } else if (char === ':') {
      const frame = top();
      if (frame?.kind === 'object') frame.expecting_key = false;
    } else if (char === '"') {
      const start = i;
      i += 1;
      while (i < text.length && text[i] !== '"') i += text[i] === '\\' ? 2 : 1;
      const frame = top();
      if (frame?.kind === 'object' && frame.expecting_key) {
        frame.current_key = JSON.parse(text.slice(start, i + 1));
        // stack[0] is the root object, which has no key.
        if (frame.current_key === 'size' && isFilePart(stack.slice(1).map((f) => f.key))) {
          offsets.push(start);
        }
      }
    }
    i += 1;
  }
  return offsets;
};
